/**
 * PWA yardımcıları — service worker kaydı + "Ana ekrana ekle" (install) akışı.
 * SW yalnız PRODUCTION build'de kaydolur (dev'de HMR ile çakışmasın).
 */
import { useEffect, useState } from "react";

/** Service worker'ı kaydet (yalnız prod). Sayfa yüklendikten sonra, sessiz hata. */
export function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (!import.meta.env.PROD) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* kayıt başarısızsa uygulama normal çalışır — sessiz geç */
    });
  });
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/** Uygulama ana ekrana kurulu (standalone) mı? */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * "Ana ekrana ekle" durumunu ve tetikleyicisini verir.
 * - Android/Chrome: `beforeinstallprompt` yakalanır → `promptInstall()` native diyaloğu açar.
 * - iOS Safari: bu olayı DESTEKLEMEZ → `isIOS` true, elle "Paylaş → Ana Ekrana Ekle" yönergesi gösterilir.
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    const onBIP = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iphone|ipad|ipod/i.test(ua);

  async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
    if (!deferred) return "unavailable";
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome;
  }

  return {
    /** Kurulu (standalone) — install UI gizlenmeli */
    installed,
    /** Android/Chrome native prompt hazır mı */
    canPrompt: !!deferred,
    /** iOS Safari (native prompt yok → elle yönerge) */
    isIOS,
    promptInstall,
  };
}
